import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";

import { useStepperContext } from "@/components/shared/Stepper/StepperContext.tsx";
import { allTravelDnaSteps } from "@/_constants/TravelDnaStep.ts";
import TravelDnaStep from "@/_constants/TravelDnaStep.ts";
import { dnaSchema } from "$/requests/UpsertDnaRequest.ts";
import WhatMakesGreatTripForYou from "$/_const/dna/WhatMakesGreatTripForYou.ts";
import type { WhatMakesGreatTripForYou as TripInterest } from "$/_const/dna/WhatMakesGreatTripForYou.ts";
import type { TravelDnaFormData } from "$/models/Dna.d.ts";
import type { OnCompleteArgs } from "@/components/functional/TravelDnaManipulationForm/TravelDnaManipulationForm.tsx";
import DnaStepLayout from "@/components/functional/TravelDnaManipulationForm/shared/DnaStepLayout.tsx";
import DnaChoiceTile from "@/components/functional/TravelDnaManipulationForm/shared/DnaChoiceTile.tsx";
import DnaPill from "@/components/functional/TravelDnaManipulationForm/shared/DnaPill.tsx";
import DnaPrimaryButton from "@/components/functional/TravelDnaManipulationForm/shared/DnaPrimaryButton.tsx";
import DnaError from "@/components/functional/TravelDnaManipulationForm/shared/DnaError.tsx";
import DnaIcon from "@/components/functional/TravelDnaManipulationForm/shared/DnaIcon.tsx";

const yourTripSchema = dnaSchema.pick({
    what_makes_great_trip_for_you: true,
    what_makes_great_trip_for_you_details: true,
    what_makes_great_trip_for_you_custom: true,
});

export type YourTripSchema = z.infer<typeof yourTripSchema>;

type YourTripProps = {
    onComplete?: (args: OnCompleteArgs) => Promise<void>;
    onCompleteWhenDirty?: (args: OnCompleteArgs) => Promise<void>;
};

const visibleInterests: TripInterest[] = [
    WhatMakesGreatTripForYou.CULTURE_AND_HISTORY,
    WhatMakesGreatTripForYou.FOOD_AND_DRINK,
    WhatMakesGreatTripForYou.NATURE_AND_WILDLIFE,
    WhatMakesGreatTripForYou.BEACHES_AND_WATER,
    WhatMakesGreatTripForYou.ART_AND_DESIGN,
    WhatMakesGreatTripForYou.WELLNESS,
    WhatMakesGreatTripForYou.NIGHTLIFE,
    WhatMakesGreatTripForYou.SHOPPING_AND_MARKETS,
    WhatMakesGreatTripForYou.ADVENTURE,
    WhatMakesGreatTripForYou.RELAXED_LEISURE,
    WhatMakesGreatTripForYou.EVENTS_AND_ENTERTAINMENT,
    WhatMakesGreatTripForYou.LOCAL_TRADITIONS,
];

const interestIcons: Partial<Record<TripInterest, ReactNode>> = {
    culture_and_history: <DnaIcon name="culture" />,
    food_and_drink: <DnaIcon name="food" />,
    nature_and_wildlife: <DnaIcon name="nature" />,
    beaches_and_water: <DnaIcon name="beach" />,
    art_and_design: <DnaIcon name="art" />,
    wellness: <DnaIcon name="wellness" />,
    nightlife: <DnaIcon name="nightlife" />,
    shopping_and_markets: <DnaIcon name="shopping" />,
    adventure: <DnaIcon name="adventure" />,
    relaxed_leisure: <DnaIcon name="relax" />,
    events_and_entertainment: <DnaIcon name="events" />,
    local_traditions: <DnaIcon name="local" />,
};

const interestDetails: Partial<Record<TripInterest, readonly string[]>> = {
    culture_and_history: [
        "architecture",
        "ancient_history",
        "museums",
        "local_traditions",
    ],
    food_and_drink: [
        "bakeries_and_cafes",
        "food_markets",
        "street_food",
        "fine_dining",
    ],
    nature_and_wildlife: [
        "gardens",
        "mountains",
        "wildlife",
        "scenic_drives",
    ],
    beaches_and_water: [
        "swimming",
        "boat_days",
        "coastal_walks",
        "watersports",
    ],
    art_and_design: [
        "galleries",
        "craft_studios",
        "design_districts",
        "photography",
    ],
    wellness: [
        "spas",
        "yoga",
        "thermal_baths",
        "slow_mornings",
    ],
    nightlife: [
        "cocktail_bars",
        "live_music",
        "clubs",
        "late_dinners",
    ],
    shopping_and_markets: [
        "independent_shops",
        "food_markets",
        "vintage",
        "local_craft",
    ],
    adventure: [
        "hiking",
        "cycling",
        "wild_swimming",
        "adventure_sports",
    ],
    relaxed_leisure: [
        "long_lunches",
        "people_watching",
        "pool_time",
        "unplanned_afternoons",
    ],
    events_and_entertainment: [
        "theatre_and_shows",
        "festivals",
        "sporting_events",
        "concerts",
        "comedy_and_live_entertainment",
    ],
    local_traditions: [
        "customs_and_etiquette",
        "folk_music_and_dance",
        "heritage_crafts",
        "community_celebrations",
    ],
};

function YourTrip({ onComplete, onCompleteWhenDirty }: YourTripProps) {
    const { t } = useTranslation();
    const { nextStep, setContextData, contextData } = useStepperContext<
        typeof allTravelDnaSteps,
        TravelDnaFormData
    >();
    const [isPending, setIsPending] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [activeInterest, setActiveInterest] = useState<TripInterest | null>(
        contextData?.what_makes_great_trip_for_you?.[0] ?? null,
    );

    const {
        register,
        handleSubmit,
        watch,
        setValue,
        formState: { errors, isDirty },
    } = useForm<YourTripSchema>({
        resolver: zodResolver(yourTripSchema),
        defaultValues: {
            what_makes_great_trip_for_you:
                contextData?.what_makes_great_trip_for_you ?? [],
            what_makes_great_trip_for_you_details:
                contextData?.what_makes_great_trip_for_you_details ?? {},
            what_makes_great_trip_for_you_custom:
                contextData?.what_makes_great_trip_for_you_custom ?? "",
        },
    });

    const selected = watch("what_makes_great_trip_for_you") ?? [];
    const details = watch("what_makes_great_trip_for_you_details") ?? {};

    const toggleInterest = (interest: TripInterest) => {
        const isSelected = selected.includes(interest);
        const nextSelected = isSelected
            ? selected.filter((item) => item !== interest)
            : [...selected, interest];

        setValue("what_makes_great_trip_for_you", nextSelected, {
            shouldDirty: true,
            shouldValidate: true,
        });

        if (isSelected) {
            const nextDetails = { ...details };
            delete nextDetails[interest];
            setValue("what_makes_great_trip_for_you_details", nextDetails, {
                shouldDirty: true,
            });
            setActiveInterest((current) =>
                current === interest ? nextSelected[0] ?? null : current
            );
        } else {
            setActiveInterest(interest);
        }
    };

    const toggleInterestDetail = (
        interest: TripInterest,
        detail: string,
    ) => {
        const current = details[interest] ?? [];

        const next = current.includes(detail)
            ? current.filter((item) => item !== detail)
            : [...current, detail];

        setValue(
            "what_makes_great_trip_for_you_details",
            {
                ...details,
                [interest]: next,
            },
            {
                shouldDirty: true,
            },
        );
    };

    const onSubmit = (data: YourTripSchema) => {
        void (async () => {
            try {
                setIsPending(true);
                setError(null);
                const cleanData: YourTripSchema = {
                    ...data,
                    what_makes_great_trip_for_you_custom:
                        data.what_makes_great_trip_for_you_custom?.trim() ||
                        null,
                };
                const context = { ...contextData, ...cleanData };
                const args: OnCompleteArgs = {
                    step: TravelDnaStep.YOUR_TRIP,
                    data: cleanData,
                    context,
                };

                if (isDirty) {
                    setContextData((previous) => ({
                        ...previous,
                        ...cleanData,
                    }));
                    await onCompleteWhenDirty?.(args);
                }

                await onComplete?.(args);
                nextStep();
            } catch (caughtError) {
                setError(
                    caughtError instanceof Error
                        ? caughtError
                        : new Error(String(caughtError)),
                );
            } finally {
                setIsPending(false);
            }
        })();
    };

    return (
        <DnaStepLayout
            eyebrow={t("travelDnaPage.yourTrip.header")}
            title={t("travelDnaPage.yourTrip.subheader")}
            description={t("travelDnaPage.yourTrip.description")}
            maxWidthClassName="max-w-[970px]"
        >
            <form
                className="mt-6 flex w-full flex-col items-center"
                onSubmit={handleSubmit(onSubmit)}
                noValidate
            >
                <DnaError error={error} className="mb-6" />

                <div className="grid w-full grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                    {visibleInterests.map((interest) => {
                        const isSelected = selected.includes(interest);
                        return (
                            <DnaChoiceTile
                                key={interest}
                                label={t(
                                    `travelDnaPage.yourTrip.options.${interest}`,
                                )}
                                icon={interestIcons[interest]}
                                selected={isSelected}
                                disabled={isPending}
                                onClick={() => toggleInterest(interest)}
                            />
                        );
                    })}
                </div>

                {selected.length > 0
                    ? (
                        <section className="mt-5 w-full">
                            <h2 className="font-sans text-sm uppercase tracking-wide text-[var(--gold)]">
                                {t("travelDnaPage.yourTrip.detailPrompt")}
                            </h2>
                            <div className="mt-3 flex flex-wrap justify-center gap-3">
                                {selected.map((interest) => {
                                    const selectedDetailCount =
                                        details[interest]?.length ?? 0;

                                    return (
                                        <DnaPill
                                            key={interest}
                                            label={t(
                                                `travelDnaPage.yourTrip.options.${interest}`,
                                            )}
                                            selected={activeInterest ===
                                                interest}
                                            badgeCount={selectedDetailCount}
                                            disabled={isPending}
                                            onClick={() =>
                                                setActiveInterest(interest)}
                                        />
                                    );
                                })}
                            </div>

                            {activeInterest &&
                                    interestDetails[activeInterest]?.length
                                ? (
                                    <div className="mt-4 flex flex-wrap justify-center gap-3">
                                        {interestDetails[activeInterest]?.map((
                                            detail,
                                        ) => (
                                            <DnaPill
                                                key={detail}
                                                label={t(
                                                    `travelDnaPage.yourTrip.details.${activeInterest}.${detail}`,
                                                )}
                                                selected={Boolean(
                                                    details[activeInterest]
                                                        ?.includes(detail),
                                                )}
                                                disabled={isPending}
                                                onClick={() =>
                                                    toggleInterestDetail(
                                                        activeInterest,
                                                        detail,
                                                    )}
                                            />
                                        ))}
                                    </div>
                                )
                                : null}
                        </section>
                    )
                    : null}

                {errors.what_makes_great_trip_for_you
                    ? (
                        <p
                            className="mt-3 w-full text-left font-sans text-sm text-error"
                            role="alert"
                        >
                            {t("travelDnaPage.yourTrip.validation.required")}
                        </p>
                    )
                    : null}

                <label className="mt-10 w-full text-left font-sans text-base text-[var(--cream)]">
                    {t("travelDnaPage.yourTrip.customLabel")}{" "}
                    <span className="rounded-full border border-[var(--gold-deep)] px-2 py-1 text-[10px] uppercase text-[var(--gold)]">
                        {t("travelDnaPage.yourTrip.optional")}
                    </span>
                    <input
                        {...register("what_makes_great_trip_for_you_custom")}
                        className="mt-3 h-12 w-full rounded-[9px] border border-[var(--gold-deep)] bg-[var(--gold-deep)] px-4 font-sans text-base font-semibold text-white outline-none placeholder:text-white/85 focus:border-[var(--gold-lt)]"
                        placeholder={t(
                            "travelDnaPage.yourTrip.customPlaceholder",
                        )}
                    />
                </label>

                <DnaPrimaryButton
                    className="mt-9"
                    loading={isPending}
                    type="submit"
                >
                    {t("travelDnaPage.yourTrip.continue")}
                </DnaPrimaryButton>
            </form>
        </DnaStepLayout>
    );
}

export default YourTrip;
